import React, { useEffect, useRef } from 'react';
import type { FC } from 'react';
import { useAsync, useExit, Spinner } from '@wix/cli-ui-kit';
import { createPackageManager, getRepoType } from '@wix/package-manager';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { match } from 'variant';
import { useProjectModel } from '../../model.js';
import {
  runAstroDev,
  EVENT_LINE,
  EVENT_SERVER_ADDRESS,
} from './utils/runAstroDev.js';
import { useAstroLogger } from './utils/useAstroLogger.js';
import { useFetchDevMetadata } from './hooks/useFetchDevMetadata.js';
import { DevEnvironmentProvider } from './DevEnvironment.js';
import { DevScreenBorder } from './DevScreenBorder.js';
import { SiteDevFlow } from './SiteDevFlow.js';
import { AppDevFlow } from './AppDevFlow.js';

interface DevCommandProps {
  port?: number;
  allowedHosts?: string;
  serverExposedUrl?: string;
}

export const DevCommand: FC<DevCommandProps> = ({
  port,
  allowedHosts,
  serverExposedUrl,
}) => {
  const {
    model: { config, projectFolder },
  } = useProjectModel();

  const exit = useExit();
  const logger = useAstroLogger({ isPrefixed: false });

  const astroProcess = useRef<ReturnType<typeof runAstroDev>>();

  const fetchDevMetadataAction = useFetchDevMetadata();

  useAsync(async () => {
    const repoType = await getRepoType(projectFolder);
    const packageManager = await createPackageManager(repoType);

    const astro = runAstroDev({
      packageManager,
      projectFolder,
      port,
      allowedHosts,
    });

    astroProcess.current = astro;

    astro.stdout?.once(EVENT_SERVER_ADDRESS, (address: string) => {
      void fetchDevMetadataAction.execute(address);
    });

    astro.stdout?.on(EVENT_LINE, (line: string) => logger.log(line));
    astro.stderr?.on(EVENT_LINE, (line: string) => logger.error(line));

    void astro.on('exit', () => {
      // If a child process is killed externally and you don’t wire Execa correctly,
      // stdio + signal handling can leave active handles open, preventing Node from exiting.
      astroProcess.current?.stdout?.destroy();
      astroProcess.current?.stderr?.destroy();
      astroProcess.current?.stdin?.end();
      astroProcess.current = undefined;

      exit(
        new CliError({
          cause: undefined,
          code: CliErrorCode.DevCommandFailed(),
        })
      );
    });
  }, []);

  useEffect(() => {
    return () => {
      if (astroProcess.current?.pid) {
        try {
          astroProcess.current.kill('SIGTERM');
        } catch {
          // Sometimes child process might exit or be killed from outside.
          // We don't want to report an error in case we fail to kill the child process.
        }
      }
    };
  }, []);

  return match(fetchDevMetadataAction.status, {
    NotRequested: () => <Spinner text="Starting dev environment..." />,
    Error: () => null,
    Loading: () => <Spinner text="Checking dev environment settings..." />,
    Success: ({ result: { devMetadata, serverLocalUrl } }) => (
      <DevScreenBorder>
        <DevEnvironmentProvider
          serverLocalUrl={serverLocalUrl}
          serverExposedUrl={serverExposedUrl}
          devMetadata={devMetadata}
        >
          {config.projectType === 'Site' ? <SiteDevFlow /> : <AppDevFlow />}
        </DevEnvironmentProvider>
      </DevScreenBorder>
    ),
  });
};
